import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Checkbox,
  Alert,
  Button,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { providersAPI, pricingAPI, addonsAPI, addonOptionsAPI, regionsAPI, defaultAddonsAPI } from '../../../services/api';
import { Provider, Addon, AddonOption, Region, AddonPricingRow } from '../../../types';

interface PriceChangeMap {
  [pricingId: string]: { monthlyCost?: number; isAvailable?: boolean };
}

const AddonPricing = () => {
  const [providers, setProviders] = useState<Provider[]>([]);
  const [selectedProvider, setSelectedProvider] = useState(0);
  const [addons, setAddons] = useState<Addon[]>([]);
  const [regions, setRegions] = useState<Region[]>([]);
  const [pricing, setPricing] = useState<AddonPricingRow[]>([]);
  const [defaultAddonIds, setDefaultAddonIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [changes, setChanges] = useState<PriceChangeMap>({});

  const fetchInitialData = async () => {
    try {
      const [providersRes, addonsRes, optionsRes] = await Promise.all([
        providersAPI.getAll(),
        addonsAPI.getAll(),
        addonOptionsAPI.getAll(),
      ]);
      setProviders(providersRes.data.filter((p: Provider) => p.IsActive));

      const activeAddons = addonsRes.data.filter((a: Addon) => a.IsActive);
      const activeOptions = optionsRes.data.filter((o: AddonOption) => o.IsActive);

      const addonIdsWithOptions = new Set(activeOptions.map((o: AddonOption) => o.AddonId));
      const addonsWithoutOptions = activeAddons.filter((a: Addon) => !addonIdsWithOptions.has(a.AddonId));

      setAddons(addonsWithoutOptions);
    } catch {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchPricingData = useCallback(async () => {
    if (providers.length === 0) return;

    try {
      const currentProvider = providers[selectedProvider];
      if (!currentProvider) return;
      const [pricingRes, regionsRes] = await Promise.all([
        pricingAPI.getAddonPricing(currentProvider.ProviderId),
        regionsAPI.getAll(currentProvider.ProviderId),
      ]);

      const activeRegions = regionsRes.data.filter((r: Region) => r.IsActive);
      setPricing(pricingRes.data);
      setRegions(activeRegions);

      const defaultAddonsPromises = activeRegions.map((region: Region) =>
        defaultAddonsAPI.getAll(currentProvider.ProviderId, region.RegionId)
      );
      const defaultAddonsResults = await Promise.all(defaultAddonsPromises);

      const allDefaultAddons = defaultAddonsResults.flatMap(res => res.data.filter((da: Record<string, unknown>) => da.IsEnabled));
      const uniqueDefaultAddonIds = [...new Set(allDefaultAddons.map((da: Record<string, unknown>) => da.AddonId as number))];
      setDefaultAddonIds(uniqueDefaultAddonIds);

      setChanges({});
    } catch {
      setError('Failed to load pricing');
    }
  }, [providers, selectedProvider]);

  useEffect(() => {
    fetchInitialData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (providers.length > 0) {
      fetchPricingData();
    }
  }, [selectedProvider, providers, fetchPricingData]);

  const handlePriceChange = (pricingId: number, field: string, value: number | boolean) => {
    setChanges({
      ...changes,
      [pricingId]: {
        ...changes[pricingId],
        [field]: value,
      },
    });
  };

  const handleSaveChanges = async () => {
    try {
      const updates = Object.entries(changes).map(([pricingId, changedFields]) => {
        const originalPricing = pricing.find(p => p.AddonPricingId === parseInt(pricingId));
        const mergedData = {
          monthlyCost: changedFields.monthlyCost !== undefined
            ? changedFields.monthlyCost
            : originalPricing?.MonthlyCost ?? 0,
          isAvailable: changedFields.isAvailable !== undefined
            ? changedFields.isAvailable
            : originalPricing?.IsAvailable ?? 0,
        };
        return pricingAPI.updateAddonPrice(pricingId, mergedData);
      });

      await Promise.all(updates);
      setSuccess('Prices updated successfully');
      setChanges({});
      fetchPricingData();

      setTimeout(() => setSuccess(''), 3000);
    } catch {
      setError('Failed to update prices');
    }
  };

  const getPriceValue = (pricingId: number, defaultValue: number) => {
    return changes[pricingId]?.monthlyCost !== undefined
      ? changes[pricingId]!.monthlyCost!
      : defaultValue;
  };

  const getAvailableValue = (pricingId: number, defaultValue: number) => {
    return changes[pricingId]?.isAvailable !== undefined
      ? changes[pricingId]!.isAvailable!
      : !!defaultValue;
  };

  const handleToggleAll = (entries: AddonPricingRow[], value: boolean) => {
    const newChanges: PriceChangeMap = { ...changes };
    entries.forEach(entry => {
      if (entry) {
        newChanges[entry.AddonPricingId] = {
          ...newChanges[entry.AddonPricingId],
          isAvailable: value,
        };
      }
    });
    setChanges(newChanges);
  };

  const isDefaultAddon = (addonId: number) => {
    return defaultAddonIds.includes(addonId);
  };

  if (loading) return <Typography>Loading...</Typography>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Addon Pricing</Typography>
        <Button
          variant="contained"
          color="secondary"
          startIcon={<SaveIcon />}
          onClick={handleSaveChanges}
          disabled={Object.keys(changes).length === 0}
        >
          Save Changes ({Object.keys(changes).length})
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={selectedProvider}
          onChange={(_e, newValue: number) => setSelectedProvider(newValue)}
          variant="fullWidth"
        >
          {providers.map((provider) => (
            <Tab key={provider.ProviderId} label={provider.ProviderName} />
          ))}
        </Tabs>
      </Paper>

      {addons.map((addon) => {
        const addonPricingEntries = pricing.filter(p => p.AddonId === addon.AddonId);

        return (
          <Paper key={addon.AddonId} sx={{ mb: 3, p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                {isDefaultAddon(addon.AddonId) && (
                  <Typography
                    component="span"
                    sx={{ fontWeight: 700, mr: 1, color: 'error.main' }}
                  >
                    (Default Addon)
                  </Typography>
                )}
                {addon.AddonName}
                {addon.Description && (
                  <Typography
                    component="span"
                    variant="body2"
                    color="text.secondary"
                    sx={{ ml: 0.5, fontWeight: 400 }}
                  >
                    &ndash; {addon.Description}
                  </Typography>
                )}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Button size="small" variant="outlined" color="success" onClick={() => handleToggleAll(addonPricingEntries, true)}>Enable All</Button>
                <Button size="small" variant="outlined" color="error" onClick={() => handleToggleAll(addonPricingEntries, false)}>Disable All</Button>
              </Box>
            </Box>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Provider</TableCell>
                    {regions.map((region) => (
                      <TableCell key={region.RegionId} align="center">
                        {region.RegionName}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>Monthly Cost ($)</TableCell>
                    {regions.map((region) => {
                      const priceEntry = addonPricingEntries.find(p => p.RegionId === region.RegionId);
                      if (!priceEntry) return <TableCell key={region.RegionId} />;

                      return (
                        <TableCell key={region.RegionId} align="center">
                          <TextField
                            type="number"
                            size="small"
                            value={getPriceValue(priceEntry.AddonPricingId, priceEntry.MonthlyCost)}
                            onChange={(e) => {
                              const value = e.target.value === '' ? 0 : parseFloat(e.target.value);
                              handlePriceChange(priceEntry.AddonPricingId, 'monthlyCost', value);
                            }}
                            inputProps={{ step: 0.01, min: 0 }}
                            sx={{
                              width: 100,
                              '& .MuiInputBase-input': {
                                fontSize: '0.875rem',
                                py: 0.5,
                                px: 1,
                              },
                            }}
                          />
                        </TableCell>
                      );
                    })}
                  </TableRow>
                  <TableRow>
                    <TableCell>Enabled</TableCell>
                    {regions.map((region) => {
                      const priceEntry = addonPricingEntries.find(p => p.RegionId === region.RegionId);
                      if (!priceEntry) return <TableCell key={region.RegionId} />;

                      return (
                        <TableCell key={region.RegionId} align="center">
                          <Checkbox
                            checked={getAvailableValue(priceEntry.AddonPricingId, priceEntry.IsAvailable)}
                            onChange={(e) =>
                              handlePriceChange(priceEntry.AddonPricingId, 'isAvailable', e.target.checked)
                            }
                          />
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        );
      })}
    </Box>
  );
};

export default AddonPricing;
